import { LegacyImages, ModelImage, Ratio } from '@easeltv/client-sdk';

import { getRatioFraction } from '..';

/* -------------------------------- Constants ------------------------------- */

/**
 * breakPoints
 *
 * A set of screen widths used for responsive layout
 */
export const breakPointsRatios: [string, Ratio][] = [
    ['base', Ratio['2:3']],
    ['768', Ratio['5:4']],
    ['1024', Ratio['16:9']],
    ['1536', Ratio['16:9']],
    ['1920', Ratio['16:7']],
    ['2100', Ratio['16:7']],
];

/* ---------------------------------- Types --------------------------------- */

/**
 * Fitting
 *
 * Whether the image should be scaled to fit, or scaled to fill.
 */
export type Fitting = 'fit' | 'fill';

/**
 * Intent
 *
 * The purpose that the image fulfils on the page. For example, tenants may decide
 * to use an epic landscape shot for the 'carousel' image, but a portrait of one of
 * the characters for the 'thumb' image.
 */
export type Intent = 'carousel' | 'hero' | 'thumb';

/**
 * BackgroundUrls
 *
 * Model of object containing breakpoints as keys to urls of image backgrounds.
 */
export type BackgroundUrls = {
    retina: { [x: string]: string };
    regular: { [x: string]: string };
};

/* -------------------------------- Functions ------------------------------- */

/**
 * buildImageUrl()
 *
 * Assembles an image URL, using parts fetched from Easel’s Falcon Heavy API.
 *
 * @param id - The rendition-id — a UUID of an image, with a particular crop and a particular intent
 * @param fitting - Whether the image should be scaled to fit, or scaled to fill
 * @param width - Image width in pixels
 * @param height - Image height in pixels
 * @param tenantCode - Usually three characters, eg 'etv', indicating the current tenant
 * @returns string - A URL to a ‘Falcon Heavy’ image, ready to be used as a `src` attribute or CSS `url(...)`
 */
export const buildImageUrl = (
    id: string,
    fitting: Fitting,
    width: number,
    height: number,
    tenantCode: string,
    env: string,
): string => {
    const base =
        // `product.links.image` is just for convenience it will always follow the below convention
        `https://image-cdn${
            env === 'prod' ? '' : `-${env}`
        }.suggestedtv.com/api/client/v1/image/rendition/`;
    const format = 'webp';
    const tenantUc = tenantCode.toUpperCase();
    return `${base}${id}?tenant=${tenantUc}&mode=${fitting}&width=${width}&height=${height}&format=${format}`;
};

/* -------------------------------------------------------------------------- */

/**
 * buildLegacyImageUrl()
 *
 * Inserts width and height into a URL fetched from Easel’s legacy images API.
 *
 * @param path - A URL from Easel’s legacy images API, containing '{width}' and '{height}'
 * @param width - Image width in pixels
 * @param height - Image height in pixels
 * @returns string - A URL to an Easel legacy image, ready to be used as a `src` attribute or CSS `url(...)`
 */
export const buildLegacyImageUrl = (
    path: string,
    width: number,
    height: number,
): string =>
    path.replace('{width}', `${width}`).replace('{height}', `${height}`);

/* -------------------------------------------------------------------------- */

/**
 * chooseImage()
 *
 * Chooses the best available image URL, given an API response and various preferences.
 *
 * @param fitting - Whether the image should be scaled to fit, or scaled to fill
 * @param intent - The purpose that the image fulfils on the page
 * @param ratio - One of our standard aspect ratios, eg '16:9'
 * @param tenantCode - Usually three characters, eg 'etv', indicating the current tenant
 * @param width - Image width in pixels
 * @param imageData - JSON fetched from Easel’s Falcon Heavy API, often a property named `image`
 * @param imageDataLegacy - JSON fetched from Easel’s legacy API, often a property named `images`
 * @returns string - A URL to an image, ready to be used as a `src` attribute or CSS `url(...)`
 *
 * @TODO remove commented-out `console.log()`
 */
export const chooseImage = (
    fitting: Fitting,
    intent: Intent,
    ratio: Ratio,
    tenantCode: string,
    env: string,
    width: number,
    imageData?: ModelImage,
    imageDataLegacy?: LegacyImages,
): string => {
    // Calculate the height, given the width.
    const widthOk = Math.round(width);
    const height = Math.round(widthOk / getRatioFraction(ratio));

    const ratioPath = imageData?.[intent]?.[ratio];
    const intentMasterPath = imageData?.[intent]?.master;
    const masterPath = imageData?.master;

    // The best choice is something like imageData.hero['16:9'].
    if (ratioPath)
        return buildImageUrl(
            ratioPath,
            fitting,
            widthOk,
            height,
            tenantCode,
            env,
        );

    // The second-best choice is the intent’s master, something like imageData.hero.master.
    if (intentMasterPath)
        return buildImageUrl(
            intentMasterPath,
            fitting,
            widthOk,
            height,
            tenantCode,
            env,
        );

    // The third-best choice is imageData.master.
    if (masterPath)
        return buildImageUrl(
            masterPath,
            fitting,
            widthOk,
            height,
            tenantCode,
            env,
        );

    // The fourth-best choice is to use the legacy (pre Falcon Heavy) images system.
    if (ratio === Ratio['21:9'] && imageDataLegacy?.hero) {
        return buildLegacyImageUrl(imageDataLegacy.hero, widthOk, height);
    }
    if (ratio === Ratio['2:3'] && imageDataLegacy?.poster) {
        return buildLegacyImageUrl(imageDataLegacy.poster, widthOk, height);
    }
    if (imageDataLegacy?.thumbnail) {
        // Not 21:9 or 2:3, so the ratio could be 4:3, 5:4, 16:9 etc.
        return buildLegacyImageUrl(imageDataLegacy.thumbnail, widthOk, height);
    }

    return '';
};

/**
 * chooseSEOImage()
 *
 * Chooses the best available image URL for SEO meta tags.
 *
 * @param fitting - Whether the image should be scaled to fit, or scaled to fill
 * @param intent - The purpose that the image fulfils on the page
 * @param ratio - One of our standard aspect ratios, eg '16:9'
 * @param tenantCode - Usually three characters, eg 'etv', indicating the current tenant
 * @param imageData - JSON fetched from Easel’s Falcon Heavy API, often a property named `image`
 * @returns string - A URL to an image, ready to be used as a `src` attribute or CSS `url(...)`
 */
export const chooseSEOImage = (
    fitting: Fitting,
    intent: Intent,
    ratio: Ratio,
    tenantCode: string,
    env: string,
    imageData?: ModelImage,
): string => {
    const ratioPath = imageData?.[intent]?.[ratio];
    const intentMasterPath = imageData?.[intent]?.master;
    const masterPath = imageData?.master;

    // The best choice is something like imageData.hero['16:9'].
    if (ratioPath)
        return buildImageUrl(ratioPath, fitting, 1200, 627, tenantCode, env);

    // The second-best choice is the intent’s master, something like imageData.hero.master.
    if (intentMasterPath)
        return buildImageUrl(
            intentMasterPath,
            fitting,
            1200,
            627,
            tenantCode,
            env,
        );

    // The third-best choice is imageData.master.
    if (masterPath)
        return buildImageUrl(masterPath, fitting, 1200, 627, tenantCode, env);

    return '';
};

/* -------------------------------------------------------------------------- */

/**
 * getHeroBackgroundUrls
 *
 * Creates a list of urls constructed from provided parameters
 *
 * @param tenant Usually a three letter string, e.g. 'etv'
 * @param env Usually a string e.g. 'dev' or 'prod'
 * @param imageData ModelImage
 * @param imageDataLegacy LegacyImages
 * @returns Object containing breakpoints as keys to urls to image urls { '768': '_image_url_', '1024': '_image_url_' }
 */
export const getHeroBackgroundUrls = ({
    tenant,
    env,
    image,
    images,
}: {
    tenant: string;
    env: string;
    image?: ModelImage;
    images?: LegacyImages;
}): BackgroundUrls => {
    const workingBreakPoints = [...breakPointsRatios];
    const fitting = 'fill';
    const intent = 'hero';
    workingBreakPoints.splice(-1, 1);

    return workingBreakPoints.reduce<BackgroundUrls>(
        ({ regular, retina }, [breakPoint, ratio], index) => ({
            regular: {
                ...regular,
                [breakPoint]: chooseImage(
                    fitting,
                    intent,
                    ratio,
                    tenant,
                    env,
                    parseInt(breakPointsRatios[index + 1][0], 10),
                    image, // Falcon Heavy image data
                    images, // legacy image data
                ),
            },
            retina: {
                ...retina,
                [breakPoint]: chooseImage(
                    fitting,
                    intent,
                    ratio,
                    tenant,
                    env,
                    parseInt(breakPointsRatios[index + 1][0], 10) * 2,
                    image, // Falcon Heavy image data
                    images, // legacy image data
                ),
            },
        }),
        { retina: {}, regular: {} },
    );
};

/* -------------------------------------------------------------------------- */

/**
 * getSlideBackgroundUrls
 *
 * Creates a list of urls constructed from provided parameters
 *
 * @param ratio Ratio
 * @param tenant Usually a three letter string, e.g. 'etv'
 * @param env Usually a string e.g. 'dev' or 'prod'
 * @param imageData ModelImage
 * @param imageDataLegacy LegacyImages
 * @returns Object containing breakpoints as keys to urls to image urls { '768': '_image_url_', '1024': '_image_url_' }
 */
export const getSlideBackgroundUrls = ({
    ratio,
    tenant,
    env,
    image,
    images,
}: {
    ratio: Ratio;
    tenant: string;
    env: string;
    image?: ModelImage;
    images?: LegacyImages;
}): BackgroundUrls => {
    const workingBreakPoints = [...breakPointsRatios];
    const fitting = 'fill';
    const intent = 'carousel';
    workingBreakPoints.splice(-1, 1);

    return workingBreakPoints.reduce<BackgroundUrls>(
        ({ regular, retina }, [breakPoint], index) => ({
            regular: {
                ...regular,
                [breakPoint]: chooseImage(
                    fitting,
                    intent,
                    ratio,
                    tenant,
                    env,
                    parseInt(breakPointsRatios[index + 1][0], 10),
                    image, // Falcon Heavy image data
                    images, // legacy image data
                ),
            },
            retina: {
                ...retina,
                [breakPoint]: chooseImage(
                    fitting,
                    intent,
                    ratio,
                    tenant,
                    env,
                    parseInt(breakPointsRatios[index + 1][0], 10) * 2,
                    image, // Falcon Heavy image data
                    images, // legacy image data
                ),
            },
        }),
        { retina: {}, regular: {} },
    );
};
